import { useLayoutEffect, useState } from "react";
import { useAtomValue } from "jotai";
import { useWindowSize } from "react-use";
import { isBannerNotificationOnAtom } from "state-atoms";
import { MIN_SCREEN_HEIGHT, MENU_ITEM_HEIGHT, FOOTER_HEIGHT, PADDING } from "../consts";

/**
 * Calculates the side menu overflow items and the main menu items.
 *
 * @param numberOfItems The number of items in the menu.
 * @param includeRecentlyUsedItem Whether to include the recently used item in the calculation.
 * @returns The index of the last item to show from the menu before it overflows, or -1 if minimum size.
 */
export function useSideMenuLayout(numberOfItems: number, includeRecentlyUsedItem: boolean) {
  const isBannerNotificationOn = useAtomValue(isBannerNotificationOnAtom);
  const size = useWindowSize();

  const [sliceIndex, setSliceIndex] = useState(numberOfItems);

  useLayoutEffect(() => {
    const headerHeight = isBannerNotificationOn ? 44 : 0;
    const recentlyUsedItemBuffer = includeRecentlyUsedItem ? 40 : 0;

    let buffer = 0;
    if (size.height < MIN_SCREEN_HEIGHT) {
      // Minimmum height
      setSliceIndex(-1);
      return;
    } else if (size.height < 340) {
      buffer = 190;
    } else if (size.height < 352) {
      buffer = 180;
    } else if (size.height < 390) {
      buffer = 170;
    } else if (size.height < 430) {
      buffer = 150;
    } else if (size.height < 440) {
      buffer = 140;
    } else if (size.height < 470) {
      buffer = 130;
    } else if (size.height < 510) {
      buffer = 120;
    } else if (size.height < 565) {
      buffer = 100;
    } else if (size.height < 600) {
      buffer = 80;
    } else if (size.height < 700) {
      buffer = 60;
    } else {
      // For sizes over 700, the buffer stays 0.
    }

    const availableHeight = size.height - buffer - recentlyUsedItemBuffer - FOOTER_HEIGHT - headerHeight - PADDING;
    const itemsToShow = Math.ceil(availableHeight / MENU_ITEM_HEIGHT);

    if (itemsToShow < numberOfItems - 2) {
      setSliceIndex(itemsToShow);
    } else {
      // No overflow
      setSliceIndex(numberOfItems);
    }
  }, [size, numberOfItems, includeRecentlyUsedItem, isBannerNotificationOn]);

  return sliceIndex;
}
